import React from "react";
import { Heading, Subheading } from "../../../design/heading";
import { ParentingPlanSection } from "../../../models/ParentingPlanSection";
import { Text } from "../../../design/text";
import { Divider } from "../../../design/divider";

interface ParentingPlanPreviewProps {
  sections: ParentingPlanSection[];
}

// Utility function to generate Roman numerals
const toRomanNumeral = (num: number): string => {
  const lookup: { [key: number]: string } = {
    1000: "M",
    900: "CM",
    500: "D",
    400: "CD",
    100: "C",
    90: "XC",
    50: "L",
    40: "XL",
    10: "X",
    9: "IX",
    5: "V",
    4: "IV",
    1: "I",
  };
  let roman = "";
  for (const value of Object.keys(lookup)
    .map(Number)
    .sort((a, b) => b - a)) {
    while (num >= value) {
      roman += lookup[value];
      num -= value;
    }
  }
  return roman;
};

// Recursive render function
const renderCheckedSections = (
  sections: ParentingPlanSection[],
  level = 0,
  parentIndex = 0,
) => {
  return sections
    .filter((section) => {
      // Show this section if:
      // 1. It's checked
      // 2. It has checked children
      const hasCheckedChildren =
        section.sections && section.sections.some((sub) => sub.isChecked);
      return section.isChecked || hasCheckedChildren;
    })
    .map((section, index) => {
      const numbering =
        level === 0
          ? `${toRomanNumeral(index + 1)}.`
          : `${String.fromCharCode(65 + index)}.`;

      return (
        <div key={section._id} className="pb-4 border-b">
          {level === 0 ? (
            <div className="flex items-center px-4 py-2 bg-gray-100 rounded">
              <Heading>{numbering}</Heading>
              <Heading level={3} className="ml-2">
                {section.title}
              </Heading>
            </div>
          ) : (
            <div className="pl-4">
              <div className="flex items-center">
                <Text className="font-semibold">{numbering}</Text>
                <Subheading className="ml-2">{section.title}</Subheading>
              </div>
            </div>
          )}

          {section.isChecked && section.body && (
            <Text className="pl-4 mt-2 whitespace-pre-wrap">
              {section.body}
            </Text>
          )}

          {section.sections && section.sections.length > 0 && (
            <div className="pl-4">
              {renderCheckedSections(section.sections, level + 1, index)}
            </div>
          )}
        </div>
      );
    });
};

export const ParentingPlanPreview: React.FC<ParentingPlanPreviewProps> = ({
  sections,
}) => {
  const checkedSectionsExist = sections.some((section) => section.isChecked);

  return (
    <div className="flex flex-col h-full p-6 bg-white">
      <Heading className="text-center">Parenting Agreement Document</Heading>
      <Text className="mb-4 text-center">
        The following sections are separate and divisible. In the event that any
        section shall be held to be invalid, the remaining sections shall
        continue to be in full force and effect. If any two or more sections are
        held to be in conflict, the earliest numbered section shall have
        precedence.
      </Text>
      <Divider className="my-4" />

      {!checkedSectionsExist ? (
        <Text>No sections selected.</Text>
      ) : (
        <div className="space-y-4">{renderCheckedSections(sections)}</div>
      )}
    </div>
  );
};
